import {useCallback, useEffect, useMemo, useState} from 'react';

const isEmpty = value => {
  if (value == null || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

/**
 * Counts how many of `names` are still empty on the given form.
 *
 * Drives the sticky action bar, so an operator sees what is outstanding
 * before they submit rather than after antd rejects the form.
 *
 * Deliberately does not use `Form.useWatch([], form)` — whole-form watching is
 * unreliable on the antd 4.x in this project. Instead the form reports changes
 * through the returned handler, and `syncKey` re-reads after a programmatic
 * `setFieldsValue` (which does not fire onValuesChange).
 *
 *   const [remaining, onValuesChange] = useRemainingFields(form, NAMES, id);
 *   <Form onValuesChange={onValuesChange} …>
 */
export const useRemainingFields = (form, names, syncKey) => {
  const [tick, setTick] = useState(0);
  const key = names.join('|');

  const remaining = useMemo(() => {
    const values = form.getFieldsValue();
    return names.filter(name => isEmpty(values[name])).length;
  }, [form, key, tick, syncKey]);

  const onValuesChange = useCallback(() => setTick(t => t + 1), []);

  // A programmatic setFieldsValue does not fire onValuesChange, and it runs in
  // an effect *after* the render that computed `remaining` — so recount once
  // the sync key settles, otherwise the bar reports stale emptiness.
  useEffect(() => {
    setTick(t => t + 1);
  }, [syncKey]);

  return [remaining, onValuesChange];
};

export default useRemainingFields;
